import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../utils/api';
import { CheckCircle2, AlertCircle, XCircle, HelpCircle } from 'lucide-react';

/**
 * Freshness Status Grid — compact per-channel data-freshness widget that
 * replaces the previous scrolling ticker. Each chip shows:
 *   - Channel name (colored chip)
 *   - Status dot: 🟢 fresh (<=7d)  🟡 stale (8–30d)  🔴 outdated (>30d)
 *   - Relative label ("2h ago", "5 days ago", "3 months ago", …)
 * Auto-sorted with the most stale first so problems catch the eye.
 *
 * Data source: /api/reviews/ticker — latest review date per channel.
 *
 * Props:
 *   - brand?   string  — scopes data to a single brand
 *   - channel? string  — scopes data to a single channel (1 chip)
 */

// Channel chip colors — matches alert email + dashboard aesthetic
const CHANNEL_COLORS = {
  Amazon:          { bg: 'rgba(251,191,36,0.10)', border: 'rgba(251,191,36,0.30)', text: '#fbbf24' },
  Flipkart:        { bg: 'rgba(59,130,246,0.10)', border: 'rgba(59,130,246,0.30)', text: '#60a5fa' },
  Zepto:           { bg: 'rgba(167,139,250,0.10)', border: 'rgba(167,139,250,0.30)', text: '#a78bfa' },
  Blinkit:         { bg: 'rgba(234,179,8,0.10)',   border: 'rgba(234,179,8,0.30)',   text: '#facc15' },
  'Wellversed.in': { bg: 'rgba(16,185,129,0.10)',  border: 'rgba(16,185,129,0.30)',  text: '#10b981' },
  Meta:            { bg: 'rgba(99,102,241,0.10)',  border: 'rgba(99,102,241,0.30)',  text: '#818cf8' },
  'Customer Support': { bg: 'rgba(244,114,182,0.10)', border: 'rgba(244,114,182,0.30)', text: '#f472b6' },
};
const DEFAULT_CHANNEL_COLOR = { bg: 'rgba(46,180,172,0.10)', border: 'rgba(46,180,172,0.30)', text: '#2eb4ac' };

// Freshness thresholds (days)
const FRESH_MAX_DAYS = 7;
const STALE_MAX_DAYS = 30;

function parseDate(str) {
  if (!str) return null;
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
}

function relativeLabel(date) {
  if (!date) return 'No data yet';
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months > 1 ? 's' : ''} ago`;
  const years = Math.floor(months / 12);
  return `${years} year${years > 1 ? 's' : ''} ago`;
}

function statusFor(date) {
  if (!date) return 'unknown';
  const days = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (days <= FRESH_MAX_DAYS) return 'fresh';
  if (days <= STALE_MAX_DAYS) return 'stale';
  return 'outdated';
}

const STATUS_STYLE = {
  fresh:    { dot: '#22c55e', text: 'text-green-400',  ring: 'shadow-[0_0_8px_rgba(34,197,94,0.5)]',   icon: CheckCircle2 },
  stale:    { dot: '#f59e0b', text: 'text-amber-400',  ring: 'shadow-[0_0_8px_rgba(245,158,11,0.5)]',  icon: AlertCircle },
  outdated: { dot: '#ef4444', text: 'text-red-400',    ring: 'shadow-[0_0_8px_rgba(239,68,68,0.5)]',   icon: XCircle },
  unknown:  { dot: '#64748b', text: 'text-slate-500',  ring: '',                                       icon: HelpCircle },
};

const STATUS_ORDER = { outdated: 0, stale: 1, unknown: 2, fresh: 3 };

export default function ReviewTicker({ brand, channel }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [iconMap, setIconMap] = useState({});

  // Fetch platform icons once — small payload, rarely changes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/api/platforms');
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const map = {};
        (Array.isArray(data) ? data : []).forEach((p) => {
          if (p?.name && p?.icon_url) map[p.name] = p.icon_url;
        });
        setIconMap(map);
      } catch (_) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchTicker = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (brand) params.set('brand', brand);
        if (channel) params.set('channel', channel);
        const qs = params.toString();
        const res = await apiFetch(`/api/reviews/ticker${qs ? `?${qs}` : ''}`);
        if (!res.ok) {
          if (!cancelled) setItems([]);
          return;
        }
        const data = await res.json();
        if (!cancelled) setItems(Array.isArray(data.items) ? data.items : []);
      } catch (_) {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchTicker();
    const id = setInterval(fetchTicker, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [brand, channel]);

  const enriched = useMemo(() => {
    const rows = items.map((it) => {
      const date = parseDate(it.latest_date) || parseDate(it.latest_month ? `${it.latest_month}-01` : null);
      const status = statusFor(date);
      return { ...it, date, status };
    });
    // Sort: worst status first, then oldest date first within a bucket
    rows.sort((a, b) => {
      const so = (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9);
      if (so !== 0) return so;
      const at = a.date ? a.date.getTime() : 0;
      const bt = b.date ? b.date.getTime() : 0;
      return at - bt; // older first
    });
    return rows;
  }, [items]);

  if (loading || enriched.length === 0) return null;

  // Overall summary counts
  const counts = enriched.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div
      className="w-full rounded-xl border border-[#2eb4ac]/25 bg-gradient-to-br from-[#2eb4ac]/[0.06] via-card/60 to-card/40 backdrop-blur-sm p-3 sm:p-4 relative overflow-hidden shadow-[inset_3px_0_0_0_#2eb4ac] transition-all hover:shadow-[inset_3px_0_0_0_#2eb4ac,0_0_24px_-8px_rgba(46,180,172,0.35)]"
      data-testid="freshness-grid"
    >
      {/* Ambient accent glow */}
      <div aria-hidden className="pointer-events-none absolute -top-16 -right-16 w-40 h-40 rounded-full bg-[#2eb4ac]/10 blur-3xl" />

      {/* Header */}
      <div className="flex items-center justify-between mb-2.5 flex-wrap gap-2 relative">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2 w-2" aria-hidden>
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#2eb4ac] opacity-60" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#2eb4ac]" />
          </span>
          <span className="text-[10px] font-semibold tracking-wider text-[#2eb4ac] uppercase">Last Updated On</span>
          <span className="text-[10px] text-muted-foreground/60">· ingest time + review coverage per channel</span>
        </div>
        <div className="flex items-center gap-3 text-[11px]" data-testid="freshness-summary">
          {counts.fresh > 0 && (
            <span className="inline-flex items-center gap-1 text-green-400" data-testid="freshness-count-fresh">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> {counts.fresh} fresh
            </span>
          )}
          {counts.stale > 0 && (
            <span className="inline-flex items-center gap-1 text-amber-400" data-testid="freshness-count-stale">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> {counts.stale} stale
            </span>
          )}
          {counts.outdated > 0 && (
            <span className="inline-flex items-center gap-1 text-red-400" data-testid="freshness-count-outdated">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500" /> {counts.outdated} outdated
            </span>
          )}
          {counts.unknown > 0 && (
            <span className="inline-flex items-center gap-1 text-slate-500" data-testid="freshness-count-unknown">
              <span className="w-1.5 h-1.5 rounded-full bg-slate-500" /> {counts.unknown} no data
            </span>
          )}
        </div>
      </div>

      {/* Grid — 2 cols on mobile, up to 6 on desktop */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {enriched.map((it) => {
          const s = STATUS_STYLE[it.status];
          const c = CHANNEL_COLORS[it.channel] || DEFAULT_CHANNEL_COLOR;
          const rel = relativeLabel(it.date);
          const tooltip = it.date
            ? `${it.channel} — last data update ${it.date.toISOString().slice(0, 10)}${it.latest_review_label ? ` · reviews through ${it.latest_review_label}` : ''}`
            : `${it.channel} — no data yet`;
          return (
            <div
              key={it.channel}
              title={tooltip}
              className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg bg-background/50 border border-border/50 hover:border-border transition-colors"
              data-testid={`freshness-item-${it.channel}`}
            >
              <span
                className={`w-2 h-2 rounded-full shrink-0 ${s.ring}`}
                style={{ backgroundColor: s.dot }}
                aria-label={it.status}
              />
              {iconMap[it.channel] ? (
                <img
                  src={iconMap[it.channel]}
                  alt=""
                  className="w-5 h-5 rounded object-cover shrink-0"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  data-testid={`freshness-logo-${it.channel}`}
                />
              ) : (
                <div
                  className="w-5 h-5 rounded flex items-center justify-center text-[9px] font-bold shrink-0"
                  style={{ backgroundColor: c.bg, color: c.text, border: `1px solid ${c.border}` }}
                  aria-hidden="true"
                >
                  {it.channel.slice(0, 1).toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div
                  className="text-[11px] font-semibold truncate leading-tight"
                  style={{ color: c.text }}
                >
                  {it.channel}
                </div>
                <div className={`text-[10px] leading-tight ${s.text}`} data-testid={`freshness-rel-${it.channel}`}>
                  {rel}
                </div>
                {it.latest_review_label && (
                  <div
                    className="text-[9px] leading-tight text-muted-foreground/70 truncate"
                    data-testid={`freshness-coverage-${it.channel}`}
                    title={`Reviews cover through ${it.latest_review_label}`}
                  >
                    till {it.latest_review_label}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
